import React, { useEffect, useState, useRef } from 'react';
import { MdOutlineGroups } from 'react-icons/md';
import { LiaUserLockSolid } from 'react-icons/lia';
import { WiTime2 } from 'react-icons/wi';
import { TbNotes } from 'react-icons/tb';
import { GoHash } from 'react-icons/go';
import { FaAt } from 'react-icons/fa';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import { IoAddOutline, IoReloadOutline } from 'react-icons/io5';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import Button from '../components/ui/Button/Button';
import { PiNotePencilBold } from 'react-icons/pi';
import TableSearchFilter from '../components/utils/TableSearchFilter';
import ResponsiveTable from '../components/Table/ResponsiveTable';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import Loading from '../components/utils/Loading';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { fetchTeamMembers } from '../redux/actions/teamMembersActions';
import { TeamMemberData } from '../redux/types/teamMembersTypes';
 
interface TeamMember {
  id: string; // This will now contain the employeeId
  employeeId: string; // Explicit employeeId field
  name: string;
  email: string;
  designation: string;
  joinDate: string;
  status: string;
}

const TeamMembers: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Get user data from auth state
  const { user } = useAppSelector(state => state.auth);

  // State for permissions management
  const [userPermissions, setUserPermissions] = useState({
    employeeRead: false,
    employeeWrite: false,
    employeeEdit: false,
    canManageEmployee: false,
  });
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  // Add refreshing state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Local state
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { teamMembers, loading, error } = useAppSelector(state => state.teamMembers);
  const [filteredTeamMembers, setFilteredTeamMembers] = useState<TeamMemberData[]>([]);

  // Use a ref to track if we've already shown the permission error
  const permissionErrorShown = useRef(false);

  // Initialize permissions from user data
  useEffect(() => {
    if (user) {
      setPermissionsLoading(true);
      // Super Admin has all permissions
      if (user.designation === 'superAdmin') {
        setUserPermissions({
          employeeRead: true,
          employeeWrite: true,
          employeeEdit: true,
          canManageEmployee: true,
        });
        setPermissionsLoading(false);
        return;
      }

      // For non-superAdmin users, check permissions
      if (user.permissions) {
        let employeeReadValue = false;
        let employeeWriteValue = false;
        let employeeEditValue = false;
        let canManageEmployeeValue = false;

        // Check if permissions is an object with numeric keys (like {0: {...}})
        if (typeof user.permissions === 'object' && '0' in user.permissions) {
          //@ts-ignore
          employeeReadValue = Boolean(user.permissions[0].employeeRead);
          //@ts-ignore
          employeeWriteValue = Boolean(user.permissions[0].employeeWrite);
          //@ts-ignore
          employeeEditValue = Boolean(user.permissions[0].employeeEdit);
          //@ts-ignore
          canManageEmployeeValue = Boolean(user.permissions[0].canManageEmployee);
        }
        // Check if permissions is an array
        else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
          employeeReadValue = Boolean(user.permissions[0].employeeRead);
          employeeWriteValue = Boolean(user.permissions[0].employeeWrite);
          employeeEditValue = Boolean(user.permissions[0].employeeEdit);
          canManageEmployeeValue = Boolean(user.permissions[0].canManageEmployee);
        }
        // Direct object access if neither of the above
        else if (typeof user.permissions === 'object') {
          employeeReadValue = Boolean(user.permissions.employeeRead);
          employeeWriteValue = Boolean(user.permissions.employeeWrite);
          employeeEditValue = Boolean(user.permissions.employeeEdit);
          canManageEmployeeValue = Boolean(user.permissions.canManageEmployee);
        }

        // Set the state with the determined values
        setUserPermissions({
          employeeRead: employeeReadValue,
          employeeWrite: employeeWriteValue,
          employeeEdit: employeeEditValue,
          canManageEmployee: canManageEmployeeValue,
        });
      }
      setPermissionsLoading(false);
    }
  }, [user]);

  // Separate useEffect for authentication check
  useEffect(() => {
    // Redirect if not authenticated
    if (user === null) {
      toast.error('Please log in to access this page', {
        duration: 3000,
        id: 'login-required', // Prevent duplicate toasts
      });
      navigate('/auth/login');
    }
  }, [user, navigate]);

  // Fetch team members on mount
  useEffect(() => {
    dispatch(fetchTeamMembers());
  }, [dispatch]);

  // Update filtered users when search value or teamMembers changes
  useEffect(() => {
    if (searchValue) {
      const lowerCaseSearch = searchValue.toLowerCase();
      const filtered = teamMembers.filter(
        member =>
          member.name.toLowerCase().includes(lowerCaseSearch) ||
          member.email.toLowerCase().includes(lowerCaseSearch) ||
          member.designation.toLowerCase().includes(lowerCaseSearch) ||
          member.status.toLowerCase().includes(lowerCaseSearch)
      );
      setFilteredTeamMembers(filtered);
    } else {
      setFilteredTeamMembers(teamMembers);
    }
  }, [searchValue, teamMembers]);

  // Handle refreshing data
  const handleRefresh = () => {
    setIsRefreshing(true);
    dispatch(fetchTeamMembers())
      .unwrap()
      .then(() => {
        toast.success('Team members data refreshed successfully');
      })
      .catch(err => {
        toast.error(err || 'Failed to refresh team members');
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  // We'll define columns directly in the ResponsiveTable component

  return (
    <ComponentWrapper
      icon={<MdOutlineGroups />}
      title="Team Members"
      buttonOne={
        <Button
          variant="outline"
          onClick={handleRefresh}
          isLoading={isRefreshing}
          loadingText="Refreshing..."
          className="flex items-center text-blue-500 border-blue-500 hover:bg-blue-50 mr-2"
        >
          <IoReloadOutline className="mr-1" />
          Refresh
        </Button>
      }
    >
      <div className="bg-white rounded-lg">
        {permissionsLoading ? (
          <div className="flex justify-center items-center h-40">
            <Loading />
          </div>
        ) : !userPermissions.employeeRead && user?.designation !== 'superAdmin' ? (
          <div className="flex flex-col items-center justify-center h-64">
            <IoMdCloseCircleOutline className="text-5xl text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-700 mb-2">Access Denied</h2>
            <p className="text-gray-500 text-center max-w-md">
              You do not have permission to view team members. Please contact your administrator for
              access.
            </p>
          </div>
        ) : (
          <>
            {/* Search and Filter */}
            <TableSearchFilter
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              showFilterPanel={showFilterPanel}
              onToggleFilterPanel={() => setShowFilterPanel(!showFilterPanel)}
              onClearSearch={() => setSearchValue('')}
              placeholder="Search team members..."
            />

            {/* Team Members Table */}
            <div className="bg-white shadow-sm rounded-lg overflow-hidden mt-4">
              <ResponsiveTable
                data={filteredTeamMembers || []}
                columns={[
                  {
                    key: 'employeeId',
                    header: 'Employee ID',
                    icon: <GoHash />,
                  },
                  {
                    key: 'name',
                    header: 'Name',
                    icon: <TbNotes />,
                  },
                  {
                    key: 'email',
                    header: 'Email',
                    icon: <FaAt />,
                  },
                  {
                    key: 'designation',
                    header: 'Designation',
                    icon: <LiaUserLockSolid />,
                    renderCell: (value, row) => {
                      // Ensure we render the designation as a string
                      const designationText =
                        typeof value === 'string' ? value : value?.name || 'No Designation';
                      return <span>{designationText}</span>;
                    },
                  },
                  {
                    key: 'joinDate',
                    header: 'Joined On',
                    icon: <WiTime2 />,
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    renderCell: (value, row) => {
                      const status = value;
                      let statusClass = '';

                      if (status === 'Active') {
                        statusClass = 'bg-green-100 text-green-800';
                      } else if (status === 'Inactive') {
                        statusClass = 'bg-red-100 text-red-800';
                      } else {
                        statusClass = 'bg-yellow-100 text-yellow-800';
                      }

                      return (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}
                        >
                          {status}
                        </span>
                      );
                    },
                  },
                ]}
                isLoading={loading || isRefreshing}
                emptyText="No team members found"
                onRowClick={row => toast.success(`Clicked on ${row?.name || 'team member'}`)}
              />
            </div>
          </>
        )}
      </div>
    </ComponentWrapper>
  );
};

export default TeamMembers;
